import React, { useState, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import moment from "moment";

const useStyles = makeStyles()(theme => ({
    wrap: { position: "relative", width: "100%" },
    svg: { display: "block", width: "100%", height: "auto", overflow: "visible" },
    baseline: { stroke: theme.palette.mode === "light" ? "#e6e4dd" : "#2b2b29", strokeWidth: 1 },
    axisText: {
        fontSize: 9.5,
        fill: theme.palette.text.disabled,
        fontVariantNumeric: "tabular-nums",
    },
    crosshair: {
        stroke: theme.palette.text.disabled,
        strokeWidth: 1,
        strokeDasharray: "3 3",
        pointerEvents: "none",
    },
    dotRing: { stroke: theme.palette.mode === "light" ? "#ffffff" : "#1a1a19", strokeWidth: 2 },
    resumen: {
        display: "flex",
        alignItems: "baseline",
        gap: theme.spacing(1),
        marginBottom: theme.spacing(0.5),
    },
    total: { fontSize: 22, fontWeight: 700, lineHeight: 1, fontVariantNumeric: "tabular-nums" },
    totalLbl: { fontSize: 11.5, color: theme.palette.text.secondary },
    tooltip: {
        position: "absolute",
        pointerEvents: "none",
        background: theme.palette.mode === "light" ? "#ffffff" : "#26262b",
        border: `1px solid ${theme.palette.mode === "light" ? "#dfe3ea" : "#33333a"}`,
        borderRadius: 6,
        padding: theme.spacing(0.5, 0.9),
        fontSize: 12,
        boxShadow: "0 2px 10px rgba(0,0,0,.14)",
        whiteSpace: "nowrap",
        zIndex: 3,
        top: -2,
    },
    tipNum: { fontWeight: 700, fontVariantNumeric: "tabular-nums" },
}));

const W = 720;
const H = 78;
const TOP = 8;
const BASE = 60;

const curva = (pts) => {
    if (pts.length === 0) return "";
    if (pts.length === 1) return `M${pts[0].x},${pts[0].y}`;
    let d = `M${pts[0].x},${pts[0].y}`;
    for (let i = 1; i < pts.length; i++) {
        const medio = { x: (pts[i - 1].x + pts[i].x) / 2, y: (pts[i - 1].y + pts[i].y) / 2 };
        d += ` Q${pts[i - 1].x},${pts[i - 1].y} ${medio.x},${medio.y}`;
    }
    const u = pts[pts.length - 1];
    d += ` L${u.x},${u.y}`;
    return d;
};

const MiniArea = ({ data, color, totalLabel, emptyLabel }) => {
    const { classes } = useStyles();
    const [hover, setHover] = useState(null);

    const calc = useMemo(() => {
        if (!data || data.length === 0) return null;
        const max = Math.max(1, ...data.map(d => d.total));
        const total = data.reduce((a, d) => a + d.total, 0);
        const paso = data.length > 1 ? W / (data.length - 1) : W;
        const pts = data.map((d, i) => ({
            x: data.length > 1 ? i * paso : W / 2,
            y: BASE - (d.total / max) * (BASE - TOP),
        }));
        const linea = curva(pts);
        const area = `${linea} L${pts[pts.length - 1].x},${BASE} L${pts[0].x},${BASE} Z`;
        return { max, total, paso, pts, linea, area };
    }, [data]);

    if (!calc) return null;
    if (calc.total === 0) {
        return <div className={classes.totalLbl}>{emptyLabel}</div>;
    }

    const mover = (e) => {
        const caja = e.currentTarget.getBoundingClientRect();
        const rel = ((e.clientX - caja.left) / caja.width) * W;
        const i = Math.round(rel / calc.paso);
        setHover(Math.max(0, Math.min(data.length - 1, i)));
    };

    const punto = hover != null ? data[hover] : null;
    const id = "areaFade";

    return (
        <div className={classes.wrap}>
            <div className={classes.resumen}>
                <span className={classes.total}>{calc.total}</span>
                <span className={classes.totalLbl}>{totalLabel}</span>
            </div>

            <svg
                className={classes.svg}
                viewBox={`0 0 ${W} ${H}`}
                preserveAspectRatio="none"
                onMouseMove={mover}
                onMouseLeave={() => setHover(null)}
                role="img"
                aria-label={totalLabel}
            >
                <defs>
                    {}
                    <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity="0.28" />
                        <stop offset="100%" stopColor={color} stopOpacity="0.02" />
                    </linearGradient>
                </defs>

                <path d={calc.area} fill={`url(#${id})`} />
                <path
                    d={calc.linea}
                    fill="none"
                    stroke={color}
                    strokeWidth={2}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                />

                <line className={classes.baseline} x1={0} x2={W} y1={BASE} y2={BASE} />

                {hover != null && (
                    <>
                        <line
                            className={classes.crosshair}
                            x1={calc.pts[hover].x}
                            x2={calc.pts[hover].x}
                            y1={TOP}
                            y2={BASE}
                        />
                        <circle
                            cx={calc.pts[hover].x}
                            cy={calc.pts[hover].y}
                            r={4}
                            fill={color}
                            className={classes.dotRing}
                        />
                    </>
                )}

                {}
                {[0, Math.floor(data.length / 2), data.length - 1].map((i) => (
                    <text
                        key={i}
                        className={classes.axisText}
                        x={calc.pts[i].x}
                        y={H - 2}
                        textAnchor={i === 0 ? "start" : i === data.length - 1 ? "end" : "middle"}
                    >
                        {moment(data[i].day).format("DD/MM")}
                    </text>
                ))}
            </svg>

            {punto && (
                <div
                    className={classes.tooltip}
                    style={{
                        left: `${(calc.pts[hover].x / W) * 100}%`,
                        transform: hover > data.length / 2 ? "translateX(-105%)" : "translateX(5%)",
                    }}
                >
                    {moment(punto.day).format("ddd DD/MM")}:{" "}
                    <span className={classes.tipNum}>{punto.total}</span>
                </div>
            )}
        </div>
    );
};

export default MiniArea;
